import React, { Suspense, useEffect } from 'react';
import Loader from '@iso/components/utility/loader';
import { useSelector } from 'react-redux';
import {
  BrowserRouter as Router,
  Redirect,
  Route,
  Switch
} from 'react-router-dom';
import ErrorBoundary from './ErrorBoundary';
import { PUBLIC_ROUTE, PRIVATE_ROUTER } from '@iso/enums';
import * as Sentry from '@sentry/react';
import lazyWithRetry from '@iso/lib/helpers/lazyWithRetry';

const Dashboard = lazyWithRetry(() =>
  import('@iso/containers/Dashboard/Dashboard')
);

const StripeOnboardingComplete = lazyWithRetry(() =>
  import('@iso/containers/StripeOnboarding/Complete')
);

const StripeOnboardingRefresh = lazyWithRetry(() =>
  import('@iso/containers/StripeOnboarding/Refresh')
);

const publicRoutes = [
  {
    path: PUBLIC_ROUTE.SIGN_IN,
    component: lazyWithRetry(() =>
      import('@iso/containers/Authentication/SignIn/SignIn')
    ),
    exact: true
  },
  {
    path: PUBLIC_ROUTE.SIGN_UP,
    component: lazyWithRetry(() =>
      import('@iso/containers/Authentication/SignUp/SignUp')
    ),
    exact: true
  },
  {
    path: PUBLIC_ROUTE.SIGN_UP_WELCOME,
    component: lazyWithRetry(() =>
      import('@iso/containers/Authentication/SignUp/Welcome')
    ),
    exact: true
  },
  {
    path: PUBLIC_ROUTE.TERM_AND_SERVICE,
    component: lazyWithRetry(() =>
      import('@iso/containers/Authentication/TermAndService/TermAndService')
    ),
    exact: true
  },
  {
    path: PUBLIC_ROUTE.FORGOT_PASSWORD,
    component: lazyWithRetry(() =>
      import('@iso/containers/Authentication/ForgotPassword/ForgotPassword')
    ),
    exact: true
  },
  {
    path: PUBLIC_ROUTE.RESET_PASSWORD,
    component: lazyWithRetry(() =>
      import('@iso/containers/Authentication/ResetPassword/ResetPassword')
    ),
    exact: true
  },
  {
    path: PUBLIC_ROUTE.RESET_PASSWORD_WELCOME,
    component: lazyWithRetry(() =>
      import('@iso/containers/Authentication/ResetPassword/Welcome')
    ),
    exact: true
  },
  {
    path: PUBLIC_ROUTE.EMAIL_VERIFY,
    component: lazyWithRetry(() =>
      import('@iso/containers/Authentication/SignUp/EmailVerification')
    )
  },
  {
    path: PUBLIC_ROUTE.ACCEPT_COMPANY_REQUEST,
    component: lazyWithRetry(() =>
      import(
        '@iso/containers/Authentication/AcceptCompanyPermission/AcceptCompanyPermission'
      )
    )
  },
  {
    path: PUBLIC_ROUTE.DECLINE_COMPANY_REQUEST,
    component: lazyWithRetry(() =>
      import(
        '@iso/containers/Authentication/DeclineCompanyPermission/DeclineCompanyPermission'
      )
    )
  },
  {
    path: PUBLIC_ROUTE.VIEW_MEMO,
    component: lazyWithRetry(() =>
      import('@iso/containers/Authentication/ViewMemo/ViewMemo')
    )
  },
  {
    path: PUBLIC_ROUTE.DECLINE_BUSINESS_MEMBERS,
    component: lazyWithRetry(() =>
      import(
        '@iso/containers/Authentication/DeclineBussinessMemeber/DeclineBussinessMemeber'
      )
    )
  },
  {
    path: PUBLIC_ROUTE.PAYMENT_INVITE,
    component: lazyWithRetry(() =>
      import('@iso/containers/Authentication/SignUp/InviteVerification')
    ),
    exact: true
  },
  {
    path: PUBLIC_ROUTE.CANCELED_PAYMENT_INVITE,
    component: lazyWithRetry(() =>
      import('@iso/containers/Authentication/SignUp/CanceledTransactionInvite')
    ),
    exact: true
  },
  {
    path: PUBLIC_ROUTE.PAGE_403,
    component: lazyWithRetry(() => import('@iso/components/ErrorPage/403/403'))
  },
  {
    path: PUBLIC_ROUTE.PAGE_404,
    component: lazyWithRetry(() => import('@iso/components/ErrorPage/404/404'))
  },
  {
    path: PUBLIC_ROUTE.PAGE_500,
    component: lazyWithRetry(() => import('@iso/components/ErrorPage/500/500'))
  },
  {
    path: PUBLIC_ROUTE.MAINTENANCE,
    component: lazyWithRetry(() =>
      import('@iso/components/ErrorPage/Maintenance/Maintenance')
    )
  },
  {
    path: PUBLIC_ROUTE.CLOSE_ACCOUNT,
    component: lazyWithRetry(() =>
      import('@iso/containers/Authentication/AccountClosed')
    ),
    exact: true
  },
  {
    path: PUBLIC_ROUTE.LANDING_PAGE,
    component: lazyWithRetry(() =>
      import('@iso/containers/LandingPage/FakeLandingPage')
    ),
    exact: true
  },
  {
    path: PUBLIC_ROUTE.MAIN_PAGE_BOOKING,
    component: lazyWithRetry(() =>
      import('@iso/containers/LandingPage/BookingCrew/BookingCrew')
    ),
    exact: true
  },
  //TODO: keep this code hid
  // {
  //   path: PUBLIC_ROUTE.GET_HIRE_FAST,
  //   component: lazyWithRetry(() =>
  //     import('@iso/containers/LandingPage/GetHiredFast/GetHiredFast')
  //   ),
  //   exact: true
  // },
  {
    path: PUBLIC_ROUTE.PAYMENT_TRACKING,
    component: lazyWithRetry(() =>
      import('@iso/containers/LandingPage/PaymentTracking/PaymentTracking')
    ),
    exact: true
  },
  {
    path: PUBLIC_ROUTE.STRIPE_LINK,
    component: lazyWithRetry(() => import('@iso/containers/StripePlan')),
    exact: true
  },
  {
    path: PUBLIC_ROUTE.BUDDI_FEED_POST,
    component: lazyWithRetry(() => import('@iso/containers/BuddiFeedPost')),
  },
  {
    path: PUBLIC_ROUTE.BUDDI_FEED_POSTS,
    component: lazyWithRetry(() => import('@iso/containers/BuddiFeedPosts')),
  },
  {
    path: PUBLIC_ROUTE.PROFILE,
    component: lazyWithRetry(() => import('@iso/containers/ShareableProfile')),
  }
];

function PrivateRoute({ children, ...rest }) {
  const isLoggedIn = useSelector((state) => state.Auth.isAuthenticated);

  return (
    <Route
      {...rest}
      render={({ location }) =>
        isLoggedIn ? (
          children
        ) : (
          <Redirect
            to={{
              pathname: PUBLIC_ROUTE.SIGN_IN,
              state: { from: location }
            }}
          />
        )
      }
    />
  );
}

const Routes = () => {
  const isLoggedIn = useSelector((state) => state.Auth.isAuthenticated);

  const { user: authUser } = useSelector((state) => state.Auth);

  const params = new URLSearchParams(window.location.search);
  const email = params.get('email');

  useEffect(() => {
    if (email) {
      if (isLoggedIn && authUser && authUser.id) {
        if (email !== authUser.email) {
          window.location.href = PUBLIC_ROUTE.PAGE_403;
          return;
        }
      }

      params.delete('email');
      window.location.search = params;
    }
  }, []);

  return (
    <Sentry.ErrorBoundary fallback={ErrorBoundary} showDialog>
      <Suspense fallback={<Loader />}>
        <Router>
          <Switch>
            {publicRoutes.map((route, index) => (
              <Route key={index} path={route.path} exact={route.exact}>
                <route.component />
              </Route>
            ))}
            {/* TODO: hidingNetwork BSKB-597 */}
            {/* <PrivateRoute path='/contact-import/callback' exact>
              <ContactImportCallback />
            </PrivateRoute> */}
            {/* <PrivateRoute
              path='/company-contact-import/:companyId/callback'
              exact
            >
              <ContactImportCallback />
            </PrivateRoute> */}
            <PrivateRoute path={PRIVATE_ROUTER.STRIPE_ONBOARDING_COMPLETE} exact>
              <StripeOnboardingComplete />
            </PrivateRoute>
            <PrivateRoute path={PRIVATE_ROUTER.STRIPE_ONBOARDING_REFRESH} exact>
              <StripeOnboardingRefresh />
            </PrivateRoute>
            <PrivateRoute path={PUBLIC_ROUTE.LANDING}>
              <Dashboard />
            </PrivateRoute>
          </Switch>
        </Router>
      </Suspense>
    </Sentry.ErrorBoundary>
  );
};

export default Routes;
